import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnInit, output, Type, untracked } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormControl, Validators } from '@angular/forms'
import { AddFromOtherEventModel } from '@app/v2/event/types/add-from-other-event.model'
import { AddFromOtherEventSelection, SourceEventOption, SourcePersonaOption, SourceSelectValue } from '@app/v2/event/types/add-from-other-event.type'
import { Event } from '@app/v2/event/types/event.type'
import { Persona } from '@app/v2/personas/types/persona.type'
import { ButtonSpinnerDirective } from '@app/v2/shared/button-loader.directive'
import { SelectInputComponent } from '@app/v2/shared/components/inputs/select-input/select-input.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { distinctUntilChanged } from 'rxjs'
import { SourceEventOptionComponent } from './components/source-event-option/source-event-option.component'
import { SourcePersonaOptionComponent } from './components/source-persona-option/source-persona-option.component'

@Component({
  selector: 'app-add-from-other-event-modal',
  standalone: true,
  imports: [ModalComponent, SelectInputComponent, ButtonSpinnerDirective],
  templateUrl: './add-from-other-event-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddFromOtherEventModalComponent implements OnInit {
  public readonly id = input.required<string>()
  public readonly events = input<Event[]>([])
  public readonly personas = input<Persona[]>([])
  public readonly currentEventId = input<number | null>(null)
  public readonly currentPersonaSlug = input<string>('')
  public readonly isLoadingEvents = input<boolean>(false)
  public readonly isLoadingPersonas = input<boolean>(false)
  public readonly isSubmitting = input<boolean>(false)

  public readonly eventChanged = output<Event | null>()
  public readonly confirmed = output<AddFromOtherEventSelection>()

  public readonly headerIcon = AddFromOtherEventModel.HEADER_PLUS_ICON
  public readonly optionsMaxHeight = AddFromOtherEventModel.OPTIONS_MAX_HEIGHT_PX
  public readonly eventControl = new FormControl<number | null>(null, Validators.required)
  public readonly personaControl = new FormControl<number | null>(null, Validators.required)

  public readonly selectedEvent = computed(() => this.events().find((event) => event.id === this._selectedEventId()) ?? null)

  public readonly availablePersonas = computed(() => {
    const isCurrentEvent = this.selectedEvent()?.id === this.currentEventId()
    if (!isCurrentEvent) return this.personas()
    return this.personas().filter((persona) => persona.slug !== this.currentPersonaSlug())
  })

  public readonly selectedPersona = computed(() => this.availablePersonas().find((persona) => persona.id === this._selectedPersonaId()) ?? null)

  public readonly eventOptions = computed((): SourceSelectValue<SourceEventOption>[] =>
    this.events().map((event) => this._toSelectValue(AddFromOtherEventModel.toEventOption(event), SourceEventOptionComponent))
  )

  public readonly personaOptions = computed((): SourceSelectValue<SourcePersonaOption>[] =>
    this.availablePersonas().map((persona) => {
      const option = AddFromOtherEventModel.toPersonaOption(persona)
      return this._toSelectValue(option, SourcePersonaOptionComponent, { disabled: option.disabled })
    })
  )

  public readonly personaNoDataMessage = computed(() => {
    if (!this.selectedEvent()) return AddFromOtherEventModel.SELECT_EVENT_FIRST_ERROR
    return this.isLoadingPersonas() ? 'Loading personas...' : 'No personas found'
  })

  public readonly canConfirm = computed(() => {
    const persona = this.selectedPersona()
    return !!this.selectedEvent() && !!persona && (persona.registration_count ?? 0) > 0 && !this.isSubmitting()
  })

  private readonly _destroyRef = inject(DestroyRef)
  private readonly _selectedEventId = toSignal(this.eventControl.valueChanges, { initialValue: null })
  private readonly _selectedPersonaId = toSignal(this.personaControl.valueChanges, { initialValue: null })
  constructor() {
    effect(() => {
      const isSubmitting = this.isSubmitting()
      untracked(() => {
        for (const control of [this.eventControl, this.personaControl]) {
          if (isSubmitting) control.disable({ emitEvent: false })
          else control.enable({ emitEvent: false })
        }
      })
    })
  }

  ngOnInit(): void {
    this.eventControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this._destroyRef))
      .subscribe((eventId) => this._onEventChange(eventId))
  }

  /**
   * Emits the selected source event and persona.
   */
  public onConfirm(): void {
    const event = this.selectedEvent()
    const persona = this.selectedPersona()
    if (!event || !persona) {
      this._showRequiredErrors()
      return
    }
    if (!this.canConfirm()) return
    this.confirmed.emit({ event, persona })
  }

  /**
   * Flags the persona field when it is used before an event is selected.
   */
  public onPersonaFieldEntered(): void {
    if (this.selectedEvent()) return
    this.personaControl.setErrors({ eventRequired: AddFromOtherEventModel.SELECT_EVENT_FIRST_ERROR })
    this.personaControl.markAsTouched()
  }

  /**
   * Shows the required error under every empty field (replaces the "Select an event first" error).
   */
  private _showRequiredErrors(): void {
    for (const control of [this.eventControl, this.personaControl]) {
      control.updateValueAndValidity({ emitEvent: false })
      control.markAsTouched()
    }
  }

  /**
   * Resets the persona field and asks the parent to load personas for the new event.
   * @param eventId {number | null} - Newly selected event id.
   */
  private _onEventChange(eventId: number | null): void {
    this.personaControl.reset(null)
    this.eventChanged.emit(this.events().find((event) => event.id === eventId) ?? null)
  }

  /**
   * Wraps an option view-model into a `select-input` value rendered by its option component.
   * @param option {T} - Event or persona option view-model.
   * @param optionComponent {Type<unknown>} - Component that renders the option row.
   * @param availability {Pick<SourcePersonaOption, 'disabled'>} - Whether the option is unselectable.
   * @returns {SourceSelectValue<T>} - Value for `select-input`.
   */
  private _toSelectValue<T extends SourceEventOption | SourcePersonaOption>(
    option: T,
    optionComponent: Type<unknown>,
    availability: Pick<SourcePersonaOption, 'disabled'> = { disabled: false }
  ): SourceSelectValue<T> {
    return { id: option.id, name: option.name, ...availability, optionComponent, optionInputs: { option } }
  }
}
